/**
 * PlayerOutModal — a small, auto-dismissing overlay shown over the map when a
 * player leaves the game, either by being eliminated in combat
 * ("<player> has been eliminated") or by resigning ("<player> has resigned").
 * No header label; the player's name is tinted in their in-game colour.
 *
 * Mirrors EndGameModal's overlay pattern (dark backdrop tap-to-dismiss + a card
 * that absorbs taps + an × close), but it is transient: auto-closes after
 * AUTO_DISMISS_MS. Shown to every live viewer the instant the pushed game state
 * grows its `eliminated` list (see lib/playerOutNotice + the game screen).
 *
 * The card is rendered with LIGHT-theme colours regardless of the active theme:
 * the player palette is tuned for the cream board and goes muddy/low-contrast
 * on a dark surface, so a fixed light card keeps every player-colour name
 * legible in dark mode too.
 */
import React, { useEffect } from 'react'
import { View, Text, Pressable, TouchableOpacity } from 'react-native'
import { PlayerColorChip, PLAYER_COLORS, clampPlayerSlot } from '@warcarp/ui'
import { useColorBySlot } from '../../lib/playerColor'
import { PLAYER_OUT_AUTO_DISMISS_MS } from '../../lib/playerOutNotice'
import { GameOverlayTopContext, inGameBackdropBounds } from '../../lib/gameOverlayBounds'
import { teamLabel, type TeamId } from '@warcarp/api-types'
import { TeamShape } from './TeamShape'

// Manual dismiss (tap) is ignored for this long after the modal appears, so a
// stray tap can't close it before it's been seen.
const GRACE_MS = 1200

// Light-theme literals (from global.css :root) so names always read.
const CARD_BG = 'rgb(244, 236, 211)' // --color-paper
const CARD_BORDER = 'rgb(74, 90, 42)' // --color-olive
const LABEL = 'rgb(107, 91, 62)' // --color-ink-soft

interface Props {
  reason: 'eliminated' | 'resigned'
  playerName: string
  playerSlot: number
  /** Eliminator name/slot — accepted for caller compatibility; the banner no
   *  longer displays the eliminator (just "<player> has been eliminated"). */
  bySlotName?: string | null
  bySlot?: number | null
  /** Team games: this exit eliminated the player's whole team. */
  teamOut?: TeamId | null
  onClose: () => void
}

export function PlayerOutModal({
  reason,
  playerName,
  playerSlot,
  teamOut,
  onClose,
}: Props) {
  // seat slot → chosen color slot (identity fallback outside a game context).
  const colorBySlot = useColorBySlot()
  const protectedTop = React.useContext(GameOverlayTopContext)
  const colorSlotFor = (s: number) => clampPlayerSlot(colorBySlot[s] ?? s)
  // Auto-dismiss. Remounting (a new notice) restarts the timer via `key`.
  useEffect(() => {
    const t = setTimeout(onClose, PLAYER_OUT_AUTO_DISMISS_MS)
    return () => clearTimeout(t)
  }, [onClose])

  // Grace period: ignore manual dismiss (backdrop / ×) for the first GRACE_MS so
  // a stray tap doesn't instantly close it. This is the eliminator's case — they
  // tap to do the free fortify the instant their attack lands, which otherwise
  // dismissed the notice before they could read it. It still auto-closes at
  // PLAYER_OUT_AUTO_DISMISS_MS regardless.
  const [dismissable, setDismissable] = React.useState(false)
  useEffect(() => {
    const t = setTimeout(() => setDismissable(true), GRACE_MS)
    return () => clearTimeout(t)
  }, [])
  const handleDismiss = () => {
    if (dismissable) onClose()
  }

  const playerHex = PLAYER_COLORS[colorSlotFor(playerSlot)].hex

  const resigned = reason === 'resigned'
  const a11y = `${playerName} has ${resigned ? 'resigned' : 'been eliminated'}${teamOut ? `. ${teamLabel(teamOut)} has been eliminated` : ''}`

  return (
    <Pressable
      onPress={handleDismiss}
      accessibilityLabel="Dismiss notice"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        ...inGameBackdropBounds(protectedTop, 24),
        backgroundColor: 'rgba(0,0,0,0.45)',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 40,
        paddingHorizontal: 24,
        paddingBottom: 24,
        overflow: 'hidden',
      }}
    >
      {/* Inner card absorbs taps so they don't bubble to the backdrop. */}
      <Pressable
        onPress={() => {}}
        accessibilityLabel={a11y}
        className="rounded-2xl px-5 py-4 items-center gap-2"
        style={{
          maxWidth: 380,
          width: '100%',
          backgroundColor: CARD_BG,
          borderWidth: 2,
          borderColor: CARD_BORDER,
        }}
      >
        <TouchableOpacity
          onPress={handleDismiss}
          accessibilityLabel="Close"
          accessibilityRole="button"
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          style={{ position: 'absolute', top: 6, right: 10, padding: 4, zIndex: 1 }}
        >
          <Text className="font-stencil text-xl" style={{ color: LABEL }}>
            ×
          </Text>
        </TouchableOpacity>

        {/* No header — just "<player> / has been eliminated" (or resigned). */}
        <View className="flex-row items-center gap-2">
          <PlayerColorChip slot={colorSlotFor(playerSlot)} size="sm" />
          <Text className="font-stencil text-lg" style={{ color: playerHex }}>
            {playerName}
          </Text>
        </View>
        <Text className="font-typed text-sm" style={{ color: LABEL }}>
          {resigned ? 'has resigned' : 'has been eliminated'}
        </Text>
        {teamOut && (
          // Team games: the last member of the team went out, marked with the team's shape.
          <View className="flex-row items-center gap-2 mt-1">
            <TeamShape team={teamOut} size={18} fill={CARD_BORDER} />
            <Text testID="player-out-team" className="font-stencil text-base uppercase" style={{ color: CARD_BORDER }}>
              {teamLabel(teamOut)} has been eliminated
            </Text>
          </View>
        )}
      </Pressable>
    </Pressable>
  )
}
